import { useTranslation } from "react-i18next";
import { CaretDown } from "@phosphor-icons/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { chapters } from "./curriculumData";

export const Curriculum = () => {
  const { t } = useTranslation();

  return (
    <section
      data-testid="programme-curriculum"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {t("programme.curriculum.kicker")}
          </p>
          <h2 className="mt-4 font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            {t("programme.curriculum.title")}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">
            {t("programme.curriculum.subtitle")}
          </p>
        </div>

        <div className="mt-16">
          <Accordion
            type="single"
            collapsible
            defaultValue={chapters[0]?.id}
            className="space-y-3"
          >
            {chapters.map((ch) => (
              <AccordionItem
                key={ch.id}
                value={ch.id}
                data-testid={`curriculum-chapter-${ch.num}`}
                className="group/item relative rounded-2xl border-2 border-slate-200 bg-white overflow-hidden transition-all duration-300 data-[state=open]:border-transparent data-[state=open]:shadow-[0_20px_50px_-20px_rgba(123,97,255,0.25)] data-[state=open]:adonis-gradient-card"
              >
                <AccordionTrigger
                  className="group px-4 md:px-7 py-4 md:py-6 hover:no-underline [&>svg:last-child]:hidden"
                  data-testid={`curriculum-trigger-${ch.num}`}
                >
                  <div className="flex flex-1 items-center gap-3 md:gap-5 text-left min-w-0">
                    <span className="relative inline-flex h-10 w-10 md:h-12 md:w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 font-heading text-sm font-bold text-slate-500 transition-all group-data-[state=open]/item:adonis-gradient group-data-[state=open]/item:text-white group-data-[state=open]/item:shadow-lg group-data-[state=open]/item:shadow-[#7B61FF]/25">
                      {ch.num}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading text-sm md:text-lg lg:text-xl font-bold tracking-tight text-slate-900 leading-snug">
                        {t(`programme.curriculum.${ch.id}_title`)}
                      </p>
                      <p className="mt-1 text-xs md:text-sm text-slate-500 font-medium leading-snug">
                        {t(`programme.curriculum.${ch.id}_subtitle`)}
                      </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-3 shrink-0">
                      <span className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1 text-xs font-bold text-slate-700 group-data-[state=open]/item:adonis-gradient group-data-[state=open]/item:text-white group-data-[state=open]/item:border-transparent transition-colors">
                        {ch.modules.length}{" "}
                        {t("programme.curriculum.modules_label")}
                      </span>
                    </div>
                    <span className="inline-flex h-8 w-8 md:h-9 md:w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white transition-transform duration-300 group-data-[state=open]/item:rotate-180 group-data-[state=open]/item:border-transparent group-data-[state=open]/item:adonis-gradient">
                      <CaretDown
                        size={14}
                        weight="bold"
                        className="text-slate-700 group-data-[state=open]/item:text-white"
                      />
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 md:px-7 pb-6 md:pb-7">
                  <div className="pt-2 pl-0 md:pl-16">
                    <p className="text-sm leading-relaxed text-slate-600 mb-6 max-w-2xl">
                      {t(`programme.curriculum.${ch.id}_intro`)}
                    </p>
                    <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-3 list-none">
                      {ch.modules.map((mKey, idx) => (
                        <li
                          key={mKey}
                          data-testid={`curriculum-module-${ch.num}-${idx + 1}`}
                          className="flex items-start gap-3 text-sm text-slate-700"
                        >
                          <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md adonis-gradient text-[10px] font-bold text-white">
                            {String(idx + 1).padStart(2, "0")}
                          </span>
                          <span className="break-words">
                            {t(`programme.curriculum.${ch.id}_m${idx + 1}`)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default Curriculum;
